import { useContext, useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { todayStartOfTheDay } from 'src/utils/date'
import { hicContext } from './context'
import { ContextProperty, ContextTenant } from './context.types'
import { IstatCapabilities, IstatProcedureCode, TsPayOnboardingState, useTsPayOnboardingStateQuery } from 'src/api/api.generated'

export const useTenant = (): ContextTenant => {
  const { context } = useContext(hicContext)
  const navigate = useNavigate()

  useEffect(() => {
    if (!context.loading && context.tenants.length === 0) {
      navigate('/')
    }
  }, [context, navigate])

  if (context.loading) {
    throw new Error('Unexpected useTenant call during tenants loading.')
  } else {
    return context.currentTenant
  }
}

export const useProperty = (): (ContextProperty & { cityTaxIsConfigured: boolean }) | null => {
  const { context } = useContext(hicContext)
  const navigate = useNavigate()

  useEffect(() => {
    if (!context.loading && context.tenants.length !== 0 && context.currentTenant?.backoffice !== true && context.properties.filter((p) => p.tenantId === context.currentTenant.id).length === 0) {
      navigate('/onboarding-property')
    }
  }, [context, navigate])

  if (!context.loading && context.currentProperty) return { ...context.currentProperty, cityTaxIsConfigured: context.cityTaxIsConfigured }
  else return null
}

export const useRequiredProperty = (): ContextProperty & {
  cityTaxIsConfigured: boolean
  istatIsConfigured: boolean
  psIsConfigured: boolean
  currentPropertyIstatCapabilities: Map<IstatProcedureCode, IstatCapabilities> | null
} => {
  const { context } = useContext(hicContext)
  if (context.loading || !context.currentProperty) {
    throw new Error("User reached a component that requires a property with null 'currentProperty' in context")
  } else {
    return {
      ...context.currentProperty,
      cityTaxIsConfigured: context.cityTaxIsConfigured,
      istatIsConfigured: context.istatIsConfigured,
      psIsConfigured: context.psIsConfigured,
      currentPropertyIstatCapabilities: context.currentPropertyIstatCapabilities,
    }
  }
}

export function useHotelDate() {
  const { context } = useContext(hicContext)

  if (!context.loading && context.currentProperty?.hotelDate) {
    return context.currentProperty.hotelDate
  } else {
    return todayStartOfTheDay()
  }
}

export function useSettings() {
  const { context } = useContext(hicContext)

  if (!context.loading && context.settings) return context.settings
  else return null
}

export function useTSPayOnboardingState() {
  const { context } = useContext(hicContext)

  const { refetch } = useTsPayOnboardingStateQuery({ skip: true, notifyOnNetworkStatusChange: true })

  const [state, setState] = useState<boolean>(false)

  useEffect(() => {
    const query = async () => {
      if (!context.loading && context.currentProperty) {
        const tsPay = await refetch({ filter: { tenantId: { eq: context.currentTenant.id }, id: { eq: context.currentProperty?.id } } })
        if (tsPay.data.properties?.[0].fiscalConfigurations?.find((fiscalConfiguration) => fiscalConfiguration?.default)?.tspay?.state === TsPayOnboardingState.DONE) setState(true)
      }
    }
    query()
  }, [context])

  return state
}

/**
 * This hook allow you to trigger a callback if the property id changes.
 * @param props
 * @returns
 */
export function useOnPropertyChange(props: { onChangeHappen?: () => void }) {
  const property = useProperty()
  const [propertyId] = useState(property?.id)

  return useEffect(() => {
    if (propertyId && property && property.id && propertyId !== property.id) {
      if (props.onChangeHappen) props.onChangeHappen()
    }
  }, [property?.id, propertyId, props.onChangeHappen])
}
